import { Skeleton } from 'antd'
import React, { useState } from 'react'
import { Chiclet, type ChicletTone } from 'sutra/components/Chiclet/Chiclet'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { useCopyToolbar } from 'components/hyper-copy/UseCopyToolbar'
import { PostIcons } from 'components/post-overview/PostIcons'
import { deriveVerbatimCardFields } from 'features/ask-yogi/features/conversation/components/VerbatimCard/verbatim-card-fields'
import {
  Card,
  ChipRow,
  DateLabel,
  HeaderRow,
  IconRow,
  MetaRow,
  NameAndSource,
  QuoteBox,
  RecordTitle,
  SkeletonBar,
  SmallInitials,
  Star,
  StarRow,
} from 'features/ask-yogi/features/conversation/components/VerbatimCard/verbatim-card.styles'
import { VerbatimCardMenu } from 'features/ask-yogi/features/conversation/components/VerbatimCardMenu/VerbatimCardMenu'
import VerbatimCardProductLink from 'features/ask-yogi/features/conversation/components/VerbatimCardProductLink/VerbatimCardProductLink'
import { buildFeedbackUrl } from 'features/ask-yogi/features/conversation/utils/url-builder'
import type {
  AgentCitationPost,
  AgentPostTextTheme,
} from 'features/ask-yogi/types'
import type { VerbatimCitation } from 'features/ask-yogi/utils/message-processing'
import mixpanel from 'trackers/mixpanel'

const SENTIMENT_TONE: Record<string, ChicletTone> = {
  Positive: 'emerald',
  Negative: 'danger',
  Neutral: 'gray',
}

/** The message/conversation/filter identity a citation click reports —
 * grouped so the component's own prop count stays under the lint threshold. */
export interface VerbatimCardTrackingContext {
  messageUuid?: string
  conversationUuid?: string
  filterHash?: string
}

export interface VerbatimCardProps {
  verbatim: VerbatimCitation
  projectId: string
  /** `undefined` = citation batch still in flight, `null` = resolved-not-found. */
  post?: AgentCitationPost | null
  theme?: AgentPostTextTheme | null
  isLoading: boolean
  compact?: boolean
  tracking?: VerbatimCardTrackingContext
}

const VerbatimCard: React.FC<VerbatimCardProps> = ({
  verbatim,
  projectId,
  post,
  theme,
  isLoading,
  compact,
  tracking,
}) => {
  const fields = deriveVerbatimCardFields(post)
  const [menuOpen, setMenuOpen] = useState(false)
  const [showFullRecord, setShowFullRecord] = useState(false)
  const { ref, isHyperCopy, selectForCopy } = useCopyToolbar({
    elementName: 'ask-yogi-verbatim-card',
  })
  const recordUrl = post?.post_uuid
    ? buildFeedbackUrl(
        projectId,
        tracking?.filterHash,
        post.post_uuid,
        tracking?.conversationUuid,
      )
    : undefined

  const handleViewFullRecord = (): void => {
    mixpanel.track('askyogi_view_verbatim', {
      proj_uuid: projectId,
      post_id: verbatim.postId,
      message_uuid: tracking?.messageUuid,
      conversation_uuid: tracking?.conversationUuid,
    })
    window.open(recordUrl, '_blank', 'noopener,noreferrer')
  }

  const handleCopyImage = (): void => {
    selectForCopy()
    mixpanel.track('copy', {
      proj_uuid: projectId,
      value: 'ask-yogi-verbatim-card',
    })
  }

  return (
    <Card
      ref={ref}
      className={isHyperCopy ? 'hyper-copy-active' : 'hyper-copy'}
      onClick={() => {
        if (isHyperCopy) selectForCopy()
      }}
    >
      <HeaderRow $compact={compact}>
        {fields.initials != null && (
          <SmallInitials initials={fields.initials} />
        )}
        {isLoading ? (
          <SkeletonBar $width="120px" />
        ) : (
          <NameAndSource>
            {fields.customerCareLabel != null ? (
              <span className="name">{fields.customerCareLabel}</span>
            ) : (
              <>
                <span className="name">{fields.reviewerName}</span>
                {fields.sourceLabel && (
                  <span className="source"> on {fields.sourceLabel}</span>
                )}
              </>
            )}
          </NameAndSource>
        )}
        {!isLoading && fields.starCount != null && (
          <StarRow>
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} $filled={n <= fields.starCount!}>
                ★
              </Star>
            ))}
          </StarRow>
        )}
        {theme?.theme && (
          <ChipRow>
            <Chiclet $tone="blue" $size="sm">
              {theme.theme}
            </Chiclet>
            {theme.sentiment && (
              <Chiclet
                $tone={SENTIMENT_TONE[theme.sentiment] ?? 'gray'}
                $size="sm"
              >
                {theme.sentiment}
              </Chiclet>
            )}
          </ChipRow>
        )}
        <VerbatimCardMenu
          open={menuOpen}
          onOpenChange={setMenuOpen}
          showFullRecord={showFullRecord}
          onToggleFullRecord={() => setShowFullRecord((shown) => !shown)}
          onViewFullRecord={recordUrl ? handleViewFullRecord : undefined}
          onCopyImage={handleCopyImage}
        />
      </HeaderRow>
      <QuoteBox>
        {isLoading ? (
          <Skeleton active title={false} paragraph={{ rows: 2 }} />
        ) : showFullRecord ? (
          post ? (
            <>
              {post.title && <RecordTitle>{post.title}</RecordTitle>}
              {post.body}
            </>
          ) : (
            'Citation data not found'
          )
        ) : (
          <>&ldquo;{verbatim.quote}&rdquo;</>
        )}
        {!isLoading && (
          <MetaRow>
            {fields.dateRelative && (
              <Tooltip content={fields.dateLabel}>
                <DateLabel>{fields.dateRelative}</DateLabel>
              </Tooltip>
            )}
            {fields.productName && (
              <VerbatimCardProductLink
                projectId={projectId}
                productName={fields.productName}
                showSeparator={fields.dateRelative != null}
              />
            )}
            {post && (
              <IconRow>
                <PostIcons
                  is_verified={post.is_verified}
                  is_promoted={post.is_promoted}
                  is_recommended={post.is_recommended}
                />
              </IconRow>
            )}
          </MetaRow>
        )}
      </QuoteBox>
    </Card>
  )
}

export default VerbatimCard
